import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Chip,
  Divider,
  CircularProgress,
} from '@mui/material';
import { SmallBusinessPlan, SmallBusinessPlanService } from 'us-web-services';
import PageStyles from 'src/styles/PageStyles';
import { axiosErrorMessage } from './axiosErrorMessage';

interface RouteParams {
  planId: string;
}

function dollarsFromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function PricingPlanEdit({ match, history }: RouteComponentProps<RouteParams>) {
  const { classes } = PageStyles();
  const planId = parseInt(match.params.planId, 10);
  const planIdInvalid = !Number.isFinite(planId);

  const [plan, setPlan] = useState<SmallBusinessPlan>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [seatLimit, setSeatLimit] = useState('');

  const [newPriceDollars, setNewPriceDollars] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await SmallBusinessPlanService.findById(planId);

      if (response?.data) {
        setPlan(response.data);
        setName(response.data.name);
        setDescription(response.data.description ?? '');
        setSeatLimit(String(response.data.seatLimit));
        setNewPriceDollars(dollarsFromCents(response.data.priceCents));
      }
    } catch (e: unknown) {
      setError(axiosErrorMessage(e, 'Failed to load plan'));
    } finally {
      setLoading(false);
    }
  }, [planId]);

  useEffect(() => {
    if (planIdInvalid) {
      setLoading(false);

      return;
    }
    load();
  }, [load, planIdInvalid]);

  const saveMetadata = async () => {
    setError(null);
    setNotice(null);
    const seat = parseInt(seatLimit, 10);

    if (!name.trim()) {
      setError('Name is required');

      return;
    }
    if (!Number.isFinite(seat) || seat < 1) {
      setError('Enter a valid seat limit');

      return;
    }
    setBusy(true);
    try {
      await SmallBusinessPlanService.patch(planId, {
        name: name.trim(),
        description: description.trim() || null,
        seatLimit: seat,
      });
      setNotice('Saved');
      await load();
    } catch (e: unknown) {
      setError(axiosErrorMessage(e, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const reprice = async () => {
    setError(null);
    setNotice(null);
    const cents = Math.round(parseFloat(newPriceDollars) * 100);

    if (!Number.isFinite(cents) || cents < 1) {
      setError('Enter a valid new price');

      return;
    }
    setBusy(true);
    try {
      const response = await SmallBusinessPlanService.reprice(planId, cents);

      if (response?.data?.id) {
        history.push(`/pricing-plans/${response.data.id}`);
      }
    } catch (e: unknown) {
      setError(axiosErrorMessage(e, 'Reprice failed'));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await SmallBusinessPlanService.deactivate(planId);
      setNotice('Plan archived');
      await load();
    } catch (e: unknown) {
      setError(axiosErrorMessage(e, 'Deactivate failed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Paper className={classes.paper}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      </Paper>
    );
  }

  if (planIdInvalid) {
    return (
      <Paper className={classes.paper}>
        <Alert severity='error'>Invalid plan id: {match.params.planId}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => history.push('/pricing-plans')}>
          Back to plans
        </Button>
      </Paper>
    );
  }

  if (!plan) {
    return (
      <Paper className={classes.paper}>
        <Alert severity='error'>{error || 'Plan not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => history.push('/pricing-plans')}>
          Back to plans
        </Button>
      </Paper>
    );
  }

  const nextPriceCents = Math.round(parseFloat(newPriceDollars) * 100);
  const priceChanged = Number.isFinite(nextPriceCents) && nextPriceCents !== plan.priceCents;

  return (
    <Paper className={classes.paper}>
      <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 2 }}>
        <Typography variant='h5'>
          {plan.tier} {plan.billingInterval}
        </Typography>
        <Chip
          size='small'
          color={plan.active ? 'success' : 'default'}
          label={plan.active ? 'active' : 'archived'}
        />
      </Stack>

      <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
        Stripe product: <code>{plan.stripeProductId}</code> · Stripe price: <code>{plan.stripePriceId}</code>
      </Typography>

      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity='success' sx={{ mb: 2 }}>{notice}</Alert>}

      <Stack spacing={2} sx={{ maxWidth: 560, mb: 4 }}>
        <Typography variant='h6'>Metadata</Typography>
        <TextField
          label='Name'
          size='small'
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label='Description'
          size='small'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={2}
        />
        <TextField
          label='Seat limit'
          size='small'
          value={seatLimit}
          onChange={(e) => setSeatLimit(e.target.value)}
        />
        <Box>
          <Button variant='contained' onClick={saveMetadata} disabled={busy || !plan.active}>
            Save changes
          </Button>
        </Box>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={2} sx={{ maxWidth: 560, mb: 4 }}>
        <Typography variant='h6'>Reprice</Typography>
        <Typography variant='body2' color='text.secondary'>
          Current: ${dollarsFromCents(plan.priceCents)} · Stripe Prices are immutable — repricing creates a new plan
          row + new Stripe Price, archives the old one. Existing subscriptions keep their captured price until renewal.
        </Typography>
        <TextField
          label='New price (dollars)'
          size='small'
          value={newPriceDollars}
          onChange={(e) => setNewPriceDollars(e.target.value)}
        />
        <Box>
          <Button
            variant='contained'
            color='warning'
            onClick={reprice}
            disabled={busy || !plan.active || !priceChanged}
          >
            Reprice
          </Button>
        </Box>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Typography variant='h6'>Archive</Typography>
        <Typography variant='body2' color='text.secondary'>
          Archived plans stop appearing on the signup page. Existing subscriptions are unaffected.
        </Typography>
        <Box>
          <Button variant='outlined' color='error' onClick={deactivate} disabled={busy || !plan.active}>
            Archive plan
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

export default withRouter(PricingPlanEdit);
